/**
 * InstanceDetailsModal - Modal de detalhes/auditoria de uma instância.
 *
 * ARQUITETURA:
 * - 100% server-side via RPC `get_instance_details_safe`
 * - 3 seções: Resumo, Timeline (movimentações), Histórico de Cobrança
 * - Objetivo: permitir que o cliente audite o ciclo de vida da instância
 *
 * SEGURANÇA:
 * - Nenhum dado sensível trafega (Stripe IDs, tokens, credentials)
 * - UUIDs internos sanitizados em 8 chars uppercase
 *
 * @version 2.0.0
 * RELEASE: 2026-06-29 v2.0.0 - Padronização visual premium do header
 *                              replicando o layout do AddInstanceDialog
 *                              (gradient + ícone badge). Mantida toda a
 *                              estrutura de corpo (Resumo/Timeline/Billing)
 *                              e lógica de dados.
 * RELEASE: 2026-04-12 v1.0.0 - Versão inicial.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  Server,
  Clock,
  Inbox as InboxIcon,
  CreditCard,
  History,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Package,
  Link2,
  Unlink,
  RefreshCw,
  ShoppingCart,
  Ban,
  RotateCcw,
  Wallet,
  Info,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  useInstanceDetails,
  type InstanceTimelineEvent,
  type InstanceBillingEvent,
} from '@/hooks/useInstanceDetails';
import type { Instance } from '@/hooks/useInstances';
// Subcomponentes de auditoria compartilhados com o modal de detalhes da inbox
import { AuditSummaryRow } from '@/components/tenant/shared/audit/AuditSummaryRow';
import { AuditTimelineItem } from '@/components/tenant/shared/audit/AuditTimelineItem';


interface InstanceDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: Instance | null;
  tenantId: string;
}

/** Formata data ISO para padrão BR (com hora) */
function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}

/**
 * Cor da bolinha indicativa por status da instância
 * (substitui o badge no header do modal).
 */
function getStatusDotClass(status: string): string {
  const map: Record<string, string> = {
    active: 'bg-emerald-500',
    connected: 'bg-emerald-500',
    connecting: 'bg-blue-500',
    pending: 'bg-amber-500',
    pending_setup: 'bg-amber-500',
    disconnected: 'bg-orange-500',
    suspended: 'bg-purple-500',
    cancelled: 'bg-destructive',
    error: 'bg-destructive',
    inactive: 'bg-muted-foreground',
  };
  return map[status] || 'bg-muted-foreground';
}

/** Mapeia ícone para action_type da timeline */

function getTimelineIcon(actionType: string) {
  const map: Record<string, typeof Clock> = {
    status_change: RefreshCw,
    link: Link2,
    unlink: Unlink,
    suspend: AlertTriangle,
    reactivate: CheckCircle2,
    cancel: XCircle,
    create: Server,
    activate: CheckCircle2,
  };
  return map[actionType] || History;
}

/** Mapeia label e cor para action_type de billing */
function getBillingMeta(actionType: string): {
  label: string;
  icon: typeof Wallet;
  badgeClass: string;
} {
  const map: Record<
    string,
    { label: string; icon: typeof Wallet; badgeClass: string }
  > = {
    purchase: {
      label: 'Compra',
      icon: ShoppingCart,
      badgeClass:
        'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    },
    cancel: {
      label: 'Cancelamento',
      icon: Ban,
      badgeClass:
        'bg-destructive/10 text-destructive border-destructive/20',
    },
    cancellation: {
      label: 'Cancelamento',
      icon: Ban,
      badgeClass:
        'bg-destructive/10 text-destructive border-destructive/20',
    },
    reactivate: {
      label: 'Reativação',
      icon: RotateCcw,
      badgeClass:
        'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    },
    refund: {
      label: 'Reembolso',
      icon: Wallet,
      badgeClass:
        'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    },
    quantity_decrease: {
      label: 'Redução de qtd.',
      icon: Ban,
      badgeClass:
        'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
    },
    quantity_increase: {
      label: 'Aumento de qtd.',
      icon: ShoppingCart,
      badgeClass:
        'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    },
  };
  return (
    map[actionType] || {
      label: actionType,
      icon: Info,
      badgeClass: 'bg-muted text-muted-foreground border-muted',
    }
  );
}

/**
 * Item da timeline (agora reaproveitado do componente compartilhado
 * `AuditTimelineItem`, também usado no modal de detalhes da inbox).
 */
function TimelineItem({ event }: { event: InstanceTimelineEvent }) {
  return <AuditTimelineItem event={event} />;
}


/** Item de billing */
function BillingItem({ event }: { event: InstanceBillingEvent }) {
  const meta = getBillingMeta(event.action_type);
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-b-0">
      <div
        className={cn(
          'flex items-center justify-center w-8 h-8 rounded-lg shrink-0',
          meta.badgeClass,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={cn('text-[10px]', meta.badgeClass)}>
            {meta.label}
          </Badge>
          {event.had_stripe && (
            <Badge variant="outline" className="text-[10px] bg-muted/50">
              <CreditCard className="h-3 w-3 mr-1" />
              Stripe
            </Badge>
          )}
        </div>
        {event.notes && (
          <p className="text-xs text-foreground/80 mt-1 leading-snug">
            {event.notes}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {formatDate(event.created_at)}
          {event.performed_by && (
            <span className="ml-2 font-mono">por {event.performed_by}</span>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Linha do resumo (label + valor) — reaproveita o componente compartilhado
 * `AuditSummaryRow`, também usado no modal de detalhes da inbox.
 */
function SummaryRow(props: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return <AuditSummaryRow {...props} />;
}


export function InstanceDetailsModal({
  open,
  onOpenChange,
  instance,
  tenantId,
}: InstanceDetailsModalProps) {
  const isMobile = useIsMobile();
  const { data, isLoading, error } = useInstanceDetails(
    instance?.id ?? null,
    tenantId,
    open,
  );

  const summary = data?.summary;
  const timeline = data?.timeline ?? [];
  const billing = data?.billing ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 overflow-hidden min-h-0',
          isMobile
            ? 'w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0'
            : 'w-[95vw] max-w-3xl h-[85dvh]',
        )}
      >
        {/* Wrapper interno: garante cadeia completa de contenção flex */}
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          {/* Header premium (padrão AddInstanceDialog) */}
          <div className="shrink-0 px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base flex-wrap">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <Server className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="truncate">
                  {instance?.instance_name || 'Detalhes da Instância'}
                </span>
                {summary?.instance_code && (
                  <Badge
                    variant="outline"
                    className="font-mono text-[10px] px-1.5 py-0 h-5"
                  >
                    {summary.instance_code}
                  </Badge>
                )}
                {summary?.status && (
                  /* Status: bolinha colorida + texto na cor padrão (sem badge) */
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-foreground">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        getStatusDotClass(summary.status),
                      )}
                    />
                    {summary.status.replace(/_/g, ' ')}
                  </span>
                )}

              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Histórico completo para auditoria e rastreamento.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Conteúdo scrollável: flex-1 + h-0 + min-h-0 garante shrink correto */}
          <ScrollArea
            className="flex-1 h-0 min-h-0"
            axis="y"
            touchAxis="pan-y"
            preventScrollChaining
            reserveScrollbarSpace
          >
            <div className="px-6 py-5 pb-8 space-y-6">
            {/* Estado: loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Estado: erro */}
            {error && !isLoading && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                Não foi possível carregar os detalhes desta instância.
              </div>
            )}

            {/* Conteúdo */}
            {!isLoading && !error && summary && (
              <>
                {/* Seção 1: Resumo */}
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    Resumo
                  </h3>
                  <div className="rounded-lg border bg-muted/20 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    <SummaryRow
                      label="Provider"
                      value={summary.provider_display_name}
                    />
                    <SummaryRow
                      label="Canal"
                      value={
                        <span className="capitalize">
                          {summary.channel_type.replace(/_/g, ' ')}
                        </span>
                      }
                    />
                    <SummaryRow
                      label="Origem"
                      value={
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-muted/50"
                        >
                          <Package className="h-3 w-3 mr-1" />
                          {summary.source_type === 'plan' && 'Plano'}
                          {summary.source_type === 'addon' && 'Add-on'}
                          {summary.source_type === 'manual' && 'Manual'}
                        </Badge>
                      }
                    />
                    <SummaryRow
                      label="Status pagamento"
                      value={summary.payment_status || '—'}
                    />
                    <SummaryRow
                      label="Inbox vinculada"
                      value={
                        summary.inbox_name ? (
                          <span className="flex items-center gap-1.5">
                            <InboxIcon className="h-3 w-3" />
                            {summary.inbox_name}
                          </span>
                        ) : (
                          '—'
                        )
                      }
                    />
                    <SummaryRow
                      label="Add-on contratado"
                      value={summary.addon_name || '—'}
                    />
                    <SummaryRow
                      label="Criada em"
                      value={formatDate(summary.created_at)}
                    />
                    <SummaryRow
                      label="Liberada em"
                      value={formatDate(summary.released_at)}
                    />
                    <SummaryRow
                      label="Ativada em"
                      value={formatDate(summary.activated_at)}
                    />
                    <SummaryRow
                      label="Cancelada em"
                      value={formatDate(summary.addon_cancelled_at)}
                    />
                    <SummaryRow
                      label="Expira em"
                      value={formatDate(summary.addon_expires_at)}
                    />
                    <SummaryRow
                      label="Assinatura Stripe"
                      value={
                        summary.has_subscription ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20"
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Vinculada
                          </Badge>
                        ) : (
                          'Sem assinatura'
                        )
                      }
                    />
                  </div>
                </section>

                <Separator />

                {/* Seção 2: Timeline */}
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" />
                    Movimentações
                    <Badge variant="outline" className="text-[10px] ml-auto">
                      {timeline.length}
                    </Badge>
                  </h3>
                  {timeline.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic px-1">
                      Nenhuma movimentação registrada.
                    </p>
                  ) : (
                    // Timeline com altura fixa e scroll interno próprio.
                    // Evita que muitas movimentações empurrem a seção
                    // "Histórico de cobrança" para fora da viewport.
                    <ScrollArea
                      className="rounded-lg border bg-muted/20 h-[320px]"
                      axis="y"
                      touchAxis="pan-y"
                      preventScrollChaining
                      reserveScrollbarSpace
                    >
                      <div className="p-4">
                        {timeline.map((event) => (
                          <TimelineItem key={event.id} event={event} />
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </section>

                <Separator />

                {/* Seção 3: Histórico de cobrança */}
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                    Histórico de cobrança
                    <Badge variant="outline" className="text-[10px] ml-auto">
                      {billing.length}
                    </Badge>
                  </h3>
                  {billing.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic px-1">
                      Nenhum evento de cobrança registrado.
                    </p>
                  ) : (
                    <div className="rounded-lg border bg-muted/20 px-4">
                      {billing.map((event) => (
                        <BillingItem key={event.id} event={event} />
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
